import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import Gap from '../components/Gap'
import LoadingSpinner from '../components/LoadingSpinner'
import ImportGroupPicker from '../components/ImportGroupPicker'
import SaveDestinationSheet from '../components/SaveDestinationSheet'
import { takeAfterCreate } from '../utils/platelistCreateReturn'
import { pageCache } from '../utils/pageCache'
import { bookmarkApi, type ImportedPlaceSummary } from '../api/bookmark'
import { useImportGroupSelection } from '../hooks/useImportGroupSelection'
import { useAuthStore } from '../stores/authStore'
import { shortenSido } from '../utils/address'
import { toastUtil } from '../utils/toast'

/** 체크리스트를 한 번에 몇 개까지 그릴지 (OnboardingImportRank 와 같다) */
const PAGE_SIZE = 40

/**
 * 네이버지도·카카오맵에서 공유해 들어오는 화면.
 *
 *   /share/map?url=<지도 링크>&text=<공유 글 전체>
 *
 * 앱 → /share(SharedLink) → 여기. 하는 일은 플레이트리스트의 '장소 가져오기'
 * (ImportListModal)와 같다 — resolveSharedLink 로 링크를 해석하고, 고른
 * 곳을 어디에 담을지 물은 뒤 담는다. 생김새는 인스타 공유(InstagramShare)를
 * 따른다. 공유로 들어오는 두 화면이 서로 달라 보이면 안 된다.
 *
 * text 를 먼저 보내는 이유 — 카카오 가게 공유("[카카오맵] 타카사 https://kko.to/…")
 * 는 가게 이름이 유일한 단서다. 서버(fetchSharedList)가 글에서 주소를 직접 뽑는다.
 */
const MapLinkShare: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const myUserId = useAuthStore((s) => s.userId)
  const url = params.get('url') ?? ''
  const shared = params.get('text') || url

  /*
   * 해석 결과와 고른 것을 기억해 둔다 (InstagramShare 와 같은 장치).
   *
   * 시트의 '추가' 로 리스트를 만들고 돌아오면 이 화면이 새로 만들어진다.
   * 기억해 두지 않으면 링크를 처음부터 다시 해석해야 하는데, 장소가 많으면
   * 그게 느려서 시트 뒤가 한동안 스피너로 남는다.
   * 전에는 그 꼴을 감추려고 시트가 열리면 본문을 통째로 안 그렸고, 그래서
   * **정상적으로 담기를 눌렀을 때도 시트 뒤가 빈 화면**이었다.
   */
  const shareCacheKey = `maplink-share:${shared}`

  const [places, setPlaces] = useState<ImportedPlaceSummary[] | null>(
    () => pageCache.get<ImportedPlaceSummary[]>(shareCacheKey)
  )
  const [error, setError] = useState<string | null>(null)
  const [checked, setChecked] = useState<Set<number>>(
    () => new Set(pageCache.get<number[]>(`${shareCacheKey}:checked`) ?? [])
  )
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [isSaving, setIsSaving] = useState(false)
  const [isDestSheetOpen, setIsDestSheetOpen] = useState(false)
  const [savedPlaceIds, setSavedPlaceIds] = useState<number[]>([])
  /** 방금 만들고 돌아온 리스트 — 시트가 담고 체크한다 */
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null)

  useEffect(() => {
    if (places) pageCache.set(shareCacheKey, places)
  }, [places, shareCacheKey])
  useEffect(() => {
    pageCache.set(`${shareCacheKey}:checked`, Array.from(checked))
  }, [checked, shareCacheKey])

  /** 리스트를 새로 만들고 돌아왔으면 시트를 다시 연다 */
  useEffect(() => {
    const back = takeAfterCreate(`maplink:${shared}`)
    if (!back) return
    const ids = (back.payload as { placeIds?: number[] } | undefined)?.placeIds ?? []
    setSavedPlaceIds(ids)
    setIsDestSheetOpen(true)
    setJustCreatedId(back.createdId)
  }, [shared])
  // 묶음이 나뉜 링크(네이버 기획전)일 때만 쓰인다
  const pick = useImportGroupSelection()

  // 같은 링크로 두 번 해석하지 않는다 (StrictMode 의 이중 호출 포함)
  const requested = useRef<string | null>(null)

  useEffect(() => {
    if (shared === '' || requested.current === shared) return
    requested.current = shared
    // 기억해 둔 결과가 있으면 다시 읽지 않는다. state(places) 가 아니라
    // pageCache 를 보는 이유는 InstagramShare 와 같다 — 여기서 알고 싶은 건
    // "들어올 때 이미 있었나" 뿐이고, 방금 넣은 결과로 이 effect 가 다시
    // 돌면 안 된다.
    if (pageCache.get<ImportedPlaceSummary[]>(shareCacheKey)) return

    let alive = true
    bookmarkApi
      // 실패하면 화면 가운데에 사유를 그린다 — 토스트까지 띄우면 같은 말이 두 번
      .resolveSharedLink(shared, undefined, { silent: true })
      .then((res) => {
        if (!alive) return
        setPlaces(res.places ?? [])
        if (res.needsGroupChoice && res.groups?.length) {
          pick.start(res.groups, res.folder_name, [], {
            groupKind: res.groupKind,
            ownerName: res.ownerName,
          })
          return
        }
        // 묶음이 없으면 전부 켜 둔다 — 링크를 보낸 건 다 담고 싶어서다
        setChecked(new Set(res.place_ids))
        if (res.imported < res.total) {
          toastUtil.info(`${res.total - res.imported}군데를 찾을 수 없었어요`)
        }
      })
      .catch((e) => {
        if (!alive) return
        setError(e?.response?.data?.message ?? '장소를 찾지 못했어요')
      })

    return () => {
      alive = false
    }
    // pick 은 매번 새 객체라 넣지 않는다 (start 는 안정적인 콜백)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared, shareCacheKey])

  const chosenIds = useMemo(
    () => (pick.groups ? pick.selected : Array.from(checked)),
    [pick.groups, pick.selected, checked]
  )

  const toggle = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /*
   * 담기를 누르면 **먼저 저장하고** 시트를 연다. 어느 리스트에 넣을지는
   * 시트에서 누르는 즉시 반영된다 — 저장 버튼(BookmarkButton)과 같은 순서다.
   */
  const handleSave = async () => {
    if (chosenIds.length === 0 || isSaving) return

    setIsSaving(true)
    try {
      await bookmarkApi.savePlaces(chosenIds)
      setSavedPlaceIds(chosenIds)
      // 알림은 시트를 닫을 때 한 번만 (아직 리스트를 고르는 중이다)
      setIsDestSheetOpen(true)
    } catch {
      // 에러 토스트는 apiClient 인터셉터가 처리
    } finally {
      setIsSaving(false)
    }
  }

  /** 시트를 닫으면 저장한 장소로 보낸다 */
  const handleSheetClose = ({ pickedAny }: { pickedAny: boolean }) => {
    setIsDestSheetOpen(false)
    // 리스트에 담았으면 그때마다 "○○에 담았어요" 를 봤다. 요약까지 띄우면
    // 두 토스트가 겹친다. 아무것도 안 고르고 닫은 경우에만 알린다.
    if (!pickedAny && savedPlaceIds.length > 0) {
      toastUtil.success(`${savedPlaceIds.length}곳을 저장했어요`)
    }
    navigate(myUserId ? `/user/${myUserId}/saved-places` : '/main-home', {
      replace: true,
    })
  }

  /*
   * 담기 시트는 본문보다 먼저 그린다 — InstagramShare 와 같은 이유.
   * 아래 본문은 상태에 따라 일찍 return 하는데, 리스트를 만들고 돌아왔을 때는
   * 본문이 없는 상태라 시트까지 같이 사라진다.
   */
  const sheet = (
    <SaveDestinationSheet
      isOpen={isDestSheetOpen}
      onClose={handleSheetClose}
      placeIds={savedPlaceIds}
      justCreatedCategoryId={justCreatedId}
      returnKey={`maplink:${shared}`}
    />
  )

  if (shared === '') {
    return <Message text="네이버지도·카카오맵에서 공유해 주세요" />
  }
  if (error) {
    // 서버가 왜 안 됐는지 알려준다. 그 문구를 그대로 쓴다 —
    // 여기서 한 번 더 감싸면 같은 말이 두 줄이 된다.
    return <Message text={error} />
  }
  if (places === null) {
    return (
      <Shell>
        {/*
          스피너만 둔다 (InstagramShare 와 같다). 점 세 개가 이미 '기다리는
          중'을 말하고 있어서, 같은 말을 글로 한 번 더 하면 화면만 복잡해진다.

          -mt-20px 는 Shell 의 pt-20px 를 되돌린다. 그게 남아 있으면 스피너가
          20px 아래로 내려앉아 가운데가 아니다. 기다리는 동안은 이 화면에
          이것 하나뿐이라 정확히 가운데 있어야 한다.
        */}
        <div className="-mt-20px flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </Shell>
    )
  }

  if (places.length === 0) {
    return (
      <Shell>
        <div className="mt-24px flex-1">
          <p className="text-style-button2 text-brown-80%-main">
            가져올 장소를 찾지 못했어요
          </p>
          <Gap height={8} />
          <p className="text-14px leading-20px text-textColorText3">
            리스트가 비공개이거나 음식점이 아닌 곳만 있는 경우예요. 직접
            검색해서 담을 수 있어요.
          </p>
        </div>
        <Footer
          label="직접 검색하기"
          disabled={false}
          onPrimary={() => navigate('/search')}
        />
      </Shell>
    )
  }

  const allChecked = places.length > 0 && checked.size === places.length

  return (
    <Shell
      // 묶음 안을 보고 있으면 뒤로가기는 묶음 목록으로
      onBack={
        pick.groups && pick.openedGroupId
          ? () => pick.setOpenedGroupId(null)
          : undefined
      }
    >
      <div className="flex-1 overflow-y-auto scrollbar-hide pb-16px">
        {pick.groups ? (
          <ImportGroupPicker
            title={pick.title}
            groups={pick.groups}
            places={places}
            selectedPlaceIds={pick.selected}
            onTogglePlace={pick.togglePlace}
            onToggleGroup={pick.toggleGroup}
            onToggleAll={pick.toggleAll}
            openedGroupId={pick.openedGroupId}
            onOpenGroup={pick.setOpenedGroupId}
            groupKind={pick.groupKind}
            ownerName={pick.ownerName}
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() =>
                setChecked(
                  allChecked ? new Set() : new Set(places.map((p) => p.id))
                )
              }
              className="mb-8px text-14px text-4B3621-80% font-bold"
            >
              {allChecked ? '전체 해제' : '전체 선택'}
            </button>
            <div className="flex flex-col gap-8px">
              {places.slice(0, visibleCount).map((p) => (
                <PlaceRow
                  key={p.id}
                  place={p}
                  checked={checked.has(p.id)}
                  onClick={() => toggle(p.id)}
                />
              ))}
            </div>
            {places.length > visibleCount && (
              <button
                type="button"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="w-full mt-12px py-12px text-14px text-textColorText3"
              >
                더 보기 ({places.length - visibleCount}곳)
              </button>
            )}
          </>
        )}
      </div>

      <Footer
        label={
          chosenIds.length > 0
            ? `${chosenIds.length}곳 저장하기`
            : '저장할 곳을 골라주세요'
        }
        disabled={chosenIds.length === 0 || isSaving}
        onPrimary={handleSave}
      />

      {/* 어디에 담을지 고르는 시트 (저장한 장소 / 플레이트리스트) */}
      {sheet}
    </Shell>
  )
}

/** 하단 알약 버튼. InstagramShare 와 같은 모양이다. */
const Footer: React.FC<{
  label: string
  disabled: boolean
  onPrimary: () => void
}> = ({ label, disabled, onPrimary }) => (
  <div className="pb-24px pt-8px flex flex-col gap-12px">
    <button
      type="button"
      onClick={onPrimary}
      disabled={disabled}
      className={`w-full py-16px rounded-full font-pretendard font-700 text-16px ${
        disabled ? 'bg-E8E1D9 text-[#918063]' : 'bg-4B3621-80% text-white'
      }`}
    >
      {label}
    </button>
  </div>
)

const Shell: React.FC<{ children: React.ReactNode; onBack?: () => void }> = ({
  children,
  onBack,
}) => (
  <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
    <AppBar title="지도에서 가져오기" isBottomBorder onBack={onBack} />
    <div className="app-container flex-1 min-h-0 flex flex-col px-20px pt-20px overflow-hidden">
      {children}
    </div>
  </div>
)

/** 장소 카드 한 장. InstagramShare 의 PlaceRow 와 같은 모양이다. */
const PlaceRow: React.FC<{
  place: ImportedPlaceSummary
  checked: boolean
  onClick: () => void
}> = ({ place, checked, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={checked}
    className="w-full flex items-center gap-12px px-12px py-12px rounded-12px bg-472410-5% text-left"
  >
    <img
      src={
        checked
          ? '/images/checkbox-bold-filled.png'
          : '/images/checkbox-bold-unchecked.png'
      }
      alt=""
      className="flex-shrink-0 w-22px h-22px object-contain"
    />
    <span className="flex-1 min-w-0">
      <span className="block text-style-button2 text-brown-80%-main text-ellipsis-1">
        {place.name}
      </span>
      {place.address && (
        <span className="block text-13px text-textColorText3 text-ellipsis-1">
          {shortenSido(place.address)}
        </span>
      )}
    </span>
  </button>
)

/**
 * 가져올 게 없을 때 보여주는 한 줄.
 *
 * 생김새를 빈 상태(홈의 '아직 기록이 없습니다')와 똑같이 맞춘다 —
 * text-style-body1 / textColorText3, 그리고 빈 칸의 광학 중앙
 * (index.css 의 .empty-state-center). 같은 '보여줄 게 없다'는 상황인데
 * 화면마다 글자 크기와 위치가 다르면 다른 앱처럼 느껴진다.
 *
 * 제목+설명 두 줄이었다가 한 줄로 줄였다. 제목('읽지 못했어요')이 아래
 * 설명과 같은 말을 두 번 하고 있었다.
 *
 * empty-state-center 는 칸을 가득 채우는 요소에 걸어야 한다. 여기서는
 * flex-1 이 그 역할이다 (제 높이가 곧 칸 높이).
 */
const Message: React.FC<{ text: string }> = ({ text }) => (
  <Shell>
    <div className="flex-1 flex items-center justify-center px-16px empty-state-center">
      <p className="text-style-body1 text-textColorText3 text-center">
        {text}
      </p>
    </div>
  </Shell>
)

export default MapLinkShare
